import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { ReferralSubmitData, ExperienceLevel, AvailabilityStatus } from '../types/ReferralTypes';
import { formatCurrency } from '../utils/formatters';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface ReferralSubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReferralSubmitData) => Promise<void>;
  vacancyTitle: string;
  companyName: string;
  bountyAmount: number;
  bountyCurrency: string;
  darkMode?: boolean;
}

const EXPERIENCE_OPTIONS: { value: ExperienceLevel; label: string }[] = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'executive', label: 'Executive' },
];

const AVAILABILITY_OPTIONS: { value: AvailabilityStatus; label: string }[] = [
  { value: 'immediately', label: 'Immediately' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: 'not_looking', label: 'Not Actively Looking' },
];

const ReferralSubmitModal: React.FC<ReferralSubmitModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vacancyTitle,
  companyName,
  bountyAmount,
  bountyCurrency,
  darkMode,
}) => {
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [candidateLinkedin, setCandidateLinkedin] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extended profile fields
  const [showExtended, setShowExtended] = useState(false);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | ''>('');
  const [skillsInput, setSkillsInput] = useState('');
  const [currentRole, setCurrentRole] = useState('');
  const [availability, setAvailability] = useState<AvailabilityStatus | ''>('');
  const [portfolioUrl, setPortfolioUrl] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidateName.trim() || !candidateEmail.trim()) {
      setError('Name and email are required.');
      return;
    }

    setLoading(true);
    setError(null);

    const skills = skillsInput
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const hasExtended = experienceLevel || skills.length > 0 || currentRole.trim() || availability || portfolioUrl.trim();

    try {
      await onSubmit({
        candidate_name: candidateName.trim(),
        candidate_email: candidateEmail.trim(),
        candidate_linkedin: candidateLinkedin.trim() || undefined,
        note: note.trim() || undefined,
        type: 'referral',
        ...(hasExtended && {
          candidate_profile: {
            ...(skills.length > 0 && { skills }),
            ...(experienceLevel && { experience_level: experienceLevel }),
            ...(currentRole.trim() && { current_role: currentRole.trim() }),
            ...(availability && { availability }),
            ...(portfolioUrl.trim() && { portfolio_url: portfolioUrl.trim() }),
          },
        }),
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit referral.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div className={`modal-backdrop ${darkMode ? 'dark' : ''}`} onClick={handleBackdropClick}>
      <div className="modal-content referral-modal">
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>

        <h2>Refer Someone</h2>
        <p className="modal-subtitle">
          {vacancyTitle} at {companyName}
        </p>
        <div className="modal-bounty-badge">
          Bounty: {formatCurrency(bountyAmount, bountyCurrency)}
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="candidateName">Candidate Name *</label>
            <input
              id="candidateName"
              type="text"
              value={candidateName}
              onChange={e => setCandidateName(e.target.value)}
              placeholder="Jane Smith"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="candidateEmail">Candidate Email *</label>
            <input
              id="candidateEmail"
              type="email"
              value={candidateEmail}
              onChange={e => setCandidateEmail(e.target.value)}
              placeholder="jane@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="candidateLinkedin">LinkedIn Profile</label>
            <input
              id="candidateLinkedin"
              type="url"
              value={candidateLinkedin}
              onChange={e => setCandidateLinkedin(e.target.value)}
              placeholder="https://linkedin.com/in/janesmith"
            />
          </div>

          <div className="form-group">
            <label htmlFor="note">Why is this person a good fit?</label>
            <textarea
              id="note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Tell us why you think this candidate would be great..."
              rows={3}
            />
          </div>

          {/* Extended profile toggle */}
          <button
            type="button"
            className="extended-fields-toggle"
            onClick={() => setShowExtended(!showExtended)}
          >
            {showExtended ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            {showExtended ? 'Hide' : 'Add'} Candidate Details (Optional)
          </button>

          {showExtended && (
            <div className="extended-fields">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="refExperience">Experience Level</label>
                  <select
                    id="refExperience"
                    value={experienceLevel}
                    onChange={e => setExperienceLevel(e.target.value as ExperienceLevel)}
                  >
                    <option value="">Select...</option>
                    {EXPERIENCE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="refCurrentRole">Current Role</label>
                  <input
                    id="refCurrentRole"
                    type="text"
                    value={currentRole}
                    onChange={e => setCurrentRole(e.target.value)}
                    placeholder="Software Engineer"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="refSkills">Skills (comma-separated)</label>
                <input
                  id="refSkills"
                  type="text"
                  value={skillsInput}
                  onChange={e => setSkillsInput(e.target.value)}
                  placeholder="React, TypeScript, Node.js"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="refAvailability">Availability</label>
                  <select
                    id="refAvailability"
                    value={availability}
                    onChange={e => setAvailability(e.target.value as AvailabilityStatus)}
                  >
                    <option value="">Select...</option>
                    {AVAILABILITY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="refPortfolio">Portfolio URL</label>
                  <input
                    id="refPortfolio"
                    type="url"
                    value={portfolioUrl}
                    onChange={e => setPortfolioUrl(e.target.value)}
                    placeholder="https://portfolio.com"
                  />
                </div>
              </div>
            </div>
          )}

          <button type="submit" className="btn-primary submit-referral-btn" disabled={loading}>
            <SendIcon sx={{ fontSize: 18, marginRight: '6px' }} />
            {loading ? 'Submitting...' : 'Submit Referral'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ReferralSubmitModal;
